/**
 * The colour half of the backdrop dialect: how dark a mark is, never how big
 * or how often. Everything about size and spacing lives in `scale.ts`, and
 * keeping the two apart is what lets a motif change density per container
 * without any risk of it also changing weight.
 *
 * The geometric patterns are drawn in one colour at three weights, declared as
 * variables rather than baked into each gradient string. That is what lets a
 * recipe stay scheme-agnostic: the light and dark values are stated once,
 * here, and every motif inherits them.
 *
 * The dark values are lower than the light ones on purpose and not by
 * oversight — a near-white rule on a #242122 surface carries further than the
 * same alpha of near-black on #F2EFEF, so matching the numbers would make the
 * patterns louder in dark mode than in light.
 */

/**
 * One dial for the loudness of every motif in the set.
 *
 * ## Why this exists
 *
 * The original alphas were tuned by looking at bands whose content is cards —
 * a grid of tiles on a textured surface, where the texture only ever shows in
 * the gutters between opaque panels. On those bands they were right. They were
 * never checked against the other kind of band, where a heading and a lede sit
 * directly on the surface with nothing between them and the motif, and there
 * a rule crossing a letterform is not a backdrop any more: the eye has to
 * separate the stroke of the glyph from the stroke of the pattern, and that
 * work shows up as the copy being tiring to read long before it shows up as a
 * contrast failure. Readers reported exactly that on the source-markets band,
 * whose diagonals run straight through the title block.
 *
 * ## Why a multiplier and not six new numbers
 *
 * The three weights are a tuned *ratio* — `strong` is roughly double `ink`
 * because a speck covers less of the surface than a rule does, and the two
 * schemes sit at their own levels for the reason above. All of that is still
 * correct; it was only the overall level that was set too high. A multiplier
 * keeps every one of those relationships intact and leaves one number to
 * argue about, which is also the number to change if this still reads too
 * loud, or too faint, on a real screen.
 *
 * ## Why 0.4
 *
 * It puts a major rule about 5 of 255 levels away from the band it is drawn
 * on in light mode, down from about 13. Below roughly 4 levels a motif stops
 * being a surface and becomes a thing you can only find if you look for it;
 * above roughly 8 it starts competing with text set directly on it. The lower
 * two weights fall out of the same multiplication, which is intended — the
 * minor rules in a major/minor pair are meant to be felt rather than seen.
 *
 * Declared in the light block alone, deliberately: `applyStyles('dark', …)`
 * overrides the three inks on the same element and inherits this, so the dial
 * is one value for both schemes rather than two that can drift apart.
 */
export const PATTERN_STRENGTH = '--kabex-pattern-strength';

const STRENGTH = 0.4;

/**
 * The tuned alpha for one weight, scaled by the dial. Kept as the original
 * number times a variable rather than pre-multiplied, so the ratios the set
 * was built on are still legible in the source.
 */
const ink = (tuned: number) =>
  `rgba(var(--mui-palette-text-primaryChannel) / calc(var(${PATTERN_STRENGTH}) * ${tuned}))`;

export const INK_LIGHT = {
  [PATTERN_STRENGTH]: `${STRENGTH}`,
  '--kabex-pattern-ink-strong': ink(0.115),
  '--kabex-pattern-ink': ink(0.055),
  '--kabex-pattern-ink-faint': ink(0.028),
};

export const INK_DARK = {
  '--kabex-pattern-ink-strong': ink(0.09),
  '--kabex-pattern-ink': ink(0.04),
  '--kabex-pattern-ink-faint': ink(0.02),
};

/**
 * `strong` is not a louder pattern, it is the same pattern drawn in a mark
 * that covers less of the surface.
 *
 * A rule is continuous: every row of pixels it crosses gets the ink, so it
 * needs the least alpha of the three to read as a line. A dot or a cross is a
 * speck with nothing either side of it, and at the same alpha the eye averages
 * it into the background and sees a flat band. Roughly double the alpha buys
 * back the weight the missing area cost.
 */
export const INK_STRONG = 'var(--kabex-pattern-ink-strong)';
export const INK = 'var(--kabex-pattern-ink)';
export const INK_FAINT = 'var(--kabex-pattern-ink-faint)';
